import React, { useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../Dialog/Dialog';
import CheckBoxInputWithLabel from '../../fields/CheckBoxInput/CheckBoxInputWithLabel';
import './setting-column.scss';
import { Button, Icons } from '../BootstrapComponents/BootstrapComponents';

let timer = null;

export default function SettingColumn(props) {
  const {
    open = false,
    lockOnPopup = false,
    availableColumns = [],
    columnsOrder = [],
    addColumns = [],
    onHide = () => {},
    setColumnsOrder = () => {},
    setVisibleColumns = () => {},
    resetVisible = () => {},
    resetOrder = () => {},
    saveVisible = () => {},
    saveOrder = () => {},
    visibleColumns = {},
  } = props;
  const { formatMessage } = useIntl();
  const height = 52.5;
  const maxIndex = availableColumns.length - 1;
  const columns = availableColumns.map((e, index) => ({
    ...e,
    style: Object.assign(
      {
        height,
        top: columnsOrder.indexOf(e.id) * height,
      },
      maxIndex !== columnsOrder.indexOf(e.id) && {
        borderBottom: '1px solid #E2E8F1',
      },
    ),
  }));
  const [targetElement, setTargetElement] = useState(null);
  const [styleGhost, setStyleGhost] = useState(null);
  const [startPos, setStartPos] = useState(0);
  const [targetPos, setTargetPos] = useState(null);
  const wrapperRef = useRef(null);

  if (!open) {
    return null;
  }

  const calculateOrder = elem => {
    const y = elem.clientY - startPos - (targetPos.scrollTop - wrapperRef.current.scrollTop);
    let element = null;
    if (y < 0 && columnsOrder.indexOf(targetElement.id)) {
      element = columns.find(i => i.id === columnsOrder[0]);
    } else if (y > (columns.length * height) && columnsOrder.indexOf(targetElement.id) < (columns.length - 1)) {
      element = columns.find(i => i.id === columnsOrder[columns.length - 1]);
    } else {
      element = columns.find(i => checkRange(y, i) && i.id !== targetElement.id);
    }
    if (element) {
      const tmp = [...columnsOrder];
      const currentIndex = columnsOrder.indexOf(targetElement.id);
      const newIndex = columnsOrder.indexOf(element.id);
      const el = tmp.splice(currentIndex, 1);
      const before = tmp.splice(0, newIndex);
      const res = [...before, ...el, ...tmp];
      setColumnsOrder(res);
    }
  };

  const cancel = () => {
    resetVisible();
    resetOrder();
  };

  const save = () => {
    saveVisible(visibleColumns);
    saveOrder(columnsOrder);
    onHide();
  };

  const checkRange = (cursorY, e) => cursorY >= e.style.top && cursorY <= (e.style.top + height);

  const onMouseUp = e => {
    document.body.classList.remove('drag-cursor');
    setTargetPos(null);
    setTargetElement(null);
    setStyleGhost(null);
    clearTimeout(timer);
  };

  const onMouseMove = elem => {
    if (targetElement) {
      const top = elem.clientY - targetPos.y;
      setStyleGhost({
        top,
        left: lockOnPopup ? targetPos.left : (elem.clientX - targetPos.x),
        width: targetPos.width,
        borderBottom: '1px solid #E2E8F1',
        borderTop: '1px solid #E2E8F1',
      });
      calculateOrder(elem);
      if (wrapperRef && wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        if (timer) {
          clearTimeout(timer);
        }
        if (top < (rect.top + targetPos.height)) {
          if (top > 50) {
            wrapperRef.current.scrollTop -= 4;
          } else {
            wrapperRef.current.scrollTop -= 12;
          }
          timer = setTimeout(() => {
            onMouseMove(elem);
          }, 10);
        }
        if (top > (rect.top + rect.height - targetPos.height)) {
          if (top - (rect.top + rect.height - targetPos.height) > 50) {
            wrapperRef.current.scrollTop += 12;
          } else {
            wrapperRef.current.scrollTop += 4;
          }
          timer = setTimeout(() => {
            onMouseMove(elem);
          }, 10);
        }
      }
    }
  };

  const onMouseDown = e => {
    const selected = columns.find(c => c.id === e.target.id);
    if (selected && !selected.disablePosition) {
      document.body.classList.add('drag-cursor');
      const targetRect = e.target.getBoundingClientRect();
      const currentTargetRect = e.currentTarget.getBoundingClientRect();
      setStartPos(currentTargetRect.y);
      setTargetPos({
        y: e.clientY - targetRect.y,
        x: e.clientX - currentTargetRect.x,
        left: targetRect.left,
        width: currentTargetRect.width,
        height: targetRect.height,
        scrollTop: wrapperRef.current.scrollTop,
      });
      setTargetElement(e.target);
    }
  };

  const renderCheckBox = (item, index) => {
    let content = '';
    if (item.headerFormatter && typeof item.headerFormatter === 'function') {
      content = item.headerFormatter(content, item);
    } else {
      content = formatMessage({ id: item.text || item.dataField, defaultMessage: item.text || ' ' });
    }
    return (
      <CheckBoxInputWithLabel
        key={index}
        type="text"
        name={item.id}
        label={content}
        withLabel
        afterContent={<Icons customIcon className="move-icon" width={24} height={24} icon="move-icon" />}
        style={item.style}
        blockId={item.id}
        className={classNames(item.classNames, 'column-row')}
        addPlaceholder
        disabled={item.disableVisible}
        inputProps={{
          value: visibleColumns[item.id],
          onChange: e => {
            setVisibleColumns({ ...visibleColumns, [item.id]: e.target.value });
          },
        }}
      />
    );
  };

  function TargetElement() {
    if (!targetElement || !targetPos) {
      return null;
    }
    const elem = { ...columns.find(e => e.id === targetElement.id) };
    const props = {
      ...elem,
      classNames: 'ghost',
      style: styleGhost,
      name: targetElement.id,
      inputProps: {
        value: visibleColumns[targetElement.id],
      },
    };
    return styleGhost ? (ReactDOM.createPortal(renderCheckBox({ ...props }, 0), document.body)) : null;
  }
  const isAll = columns.every(o => visibleColumns[o.id]);
  return (
    <Dialog
      className={classNames('setting-column')}
      onClose={() => { cancel(); onHide(); }}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      open
    >
      <DialogTitle header={formatMessage({ id: 'selected.columns' })} title={<Icons onClick={() => { cancel(); onHide(); }} customIcon icon="close" className="close" />} />
      <div className="setting-column-select-all">
        <CheckBoxInputWithLabel
          type="text"
          name="select-all"
          label="Выбрать все"
          withLabel
          afterContent={(
            <Button
              className="btn-sm"
              onClick={() => {
                setVisibleColumns(columns.reduce((acc, cur) => {
                  acc[cur.id] = !addColumns.find(f => f.id === cur.id);
                  return acc;
                }, {}));
              }}
            >
              По умолчанию
            </Button>
          )}
          className=""
          addPlaceholder
          inputProps={{
            value: isAll,
            onChange: e => {
              if (isAll) {
                setVisibleColumns(columns.reduce((acc, cur) => {
                  acc[cur.id] = cur.disableVisible;
                  return acc;
                }, {}));
              } else {
                setVisibleColumns(columns.reduce((acc, cur) => {
                  acc[cur.id] = true;
                  return acc;
                }, {}));
              }
            },
          }}
        />
      </div>
      <DialogContent className="hff-content custom-scroll-theme" blockRef={wrapperRef}>
        <div className="hff-wrapper" onMouseDown={onMouseDown}>
          {columns.map(renderCheckBox)}
          <TargetElement />
        </div>
      </DialogContent>
      <DialogFooter className="hff-footer">
        <div className="hff-footer-buttons">
          <Button className="hff-clear-show" onClick={save}>{formatMessage({ id: 'app.save' })}</Button>
          <Button className="hff-clear-data" variant="secondary" onClick={cancel}>{formatMessage({ id: 'app.cancel' })}</Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
